import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Route, Routes, useLocation } from "react-router-dom";
import { DrxPage } from "../../../../pages/drx-page";

// Print DRX, opened over the Orders list instead of in a new tab.
//
// A new tab steals focus, and browsers give a page no way to open one in the background.
// So the legacy DRX page (src/pages/drx-page) is rendered HERE, unchanged, in a full-size
// overlay: print from it, close it, and the Orders list is exactly where it was.
//
// The DRX page reads `orderId` with useParams() in several of its components, and reads
// useSearchParams() to pick its batch mode. A descendant <Routes> with an explicit
// `location` gives it its own `drx/:orderId` match and an EMPTY query string, without
// touching the real URL -- the Orders page's ?page=&search= must not leak into it.
// The pathname is built under the current route because descendant <Routes> match
// relative to their parent's path.
//
// Not reachable to every console role through the legacy /drx route (its roles exclude
// SUPER_LOGISTICS), which is the other reason it is mounted here, inside the logistics
// console's own RequireRoles gate.
function DrxModal({ orderId, onClose }) {
    const { pathname } = useLocation();
    const base = pathname.replace(/\/+$/, "");

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Freeze the Orders page behind the overlay, so scrolling the DRX on a touch screen
    // does not drag the list underneath once the DRX hits its end.
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, []);

    // Sizing, by screen:
    //   phone (< sm)  full screen, no rounding -- every pixel goes to the DRX.
    //   sm and up     the viewport less a 1rem margin, 94% tall, capped at 1600px wide so
    //                 it does not sprawl across an ultrawide monitor.
    // Heights use dvh where supported, so a mobile browser's collapsing toolbar cannot push
    // the Close button off-screen; plain vh is the fallback.
    //
    // The DRX itself is NEVER scaled down to fit: it is a print layout with fixed widths
    // (the horizontal DRX is 1020px), and shrinking it on screen risks changing what
    // prints. Instead the body scrolls both ways. The DRX page ships its own
    // `max-h-[85vh] overflow-y-auto overflow-x-hidden` wrapper, built for a full page; the
    // `[&>div]` overrides below switch that off, so there is ONE scroll area (no nested
    // scrollbars) and wide tables scroll sideways instead of being clipped.
    return (
        <div
            role="presentation"
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
            className="fixed inset-0 z-[200000] flex items-stretch justify-center bg-black/50 sm:items-center sm:p-4"
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`DRX for order #${orderId}`}
                className="flex h-[100vh] w-full flex-col overflow-hidden bg-white-50 shadow-xl supports-[height:100dvh]:h-[100dvh] sm:h-[94vh] sm:max-w-[1600px] sm:rounded-2xl sm:supports-[height:100dvh]:h-[94dvh]"
            >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-3 py-2 sm:px-5 sm:py-3">
                    <span className="min-w-0 truncate text-[1.2rem] font-bold sm:text-[1.4rem]">
                        {`DRX — Order #${orderId}`}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-xl border border-gray-300 px-4 py-1.5 text-[1.1rem] font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        Close
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-auto overscroll-contain px-1 sm:px-2 [&>div]:max-h-none [&>div]:overflow-visible">
                    <Routes location={{ pathname: `${base}/drx/${orderId}`, search: "", hash: "", state: null, key: "drx-modal" }}>
                        <Route path="drx/:orderId" element={<DrxPage onBack={onClose} />} />
                    </Routes>
                </div>
            </div>
        </div>
    );
}

DrxModal.propTypes = {
    orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onClose: PropTypes.func.isRequired,
};

export default DrxModal;
